import { useState } from 'react';
import { Outlet } from '@tanstack/react-router';
import { useLocation } from '../../lib/routing';
import { HeaderShellRoot } from '../header';
import { FloatingChat } from '../ui/floating-chat';
import { ExamImportNoticeToast } from '../exam-import-progress';
import { BulkImportNoticeToast } from '../bulk-import-progress';
import AppHeader from './app-header';
import { useAppHeaderConfig } from './app-header-store';

/* Routes publiques / flux d'onboarding : ni rail, ni burger, ni CTA "Se
   connecter" du shell — ces pages possedent leur propre mise en page. */
const NO_SHELL_HEADER_PATHS = new Set([
  '/',
  '/sign-in',
  '/reset-password',
  '/invite/accept',
  '/admin-invite/accept',
  '/tutor/accept',
  '/responsable/accept',
  '/direction/onboarding',
  '/onboarding',
  '/onboarding/classes',
  '/onboarding/preferences',
]);

/* Écrans de chat plein-écran (/c/$threadId, /teacher/chat, /admin/chat,
   /tutor/enfant/$id/chat) : un second bouton de chat flottant par-dessus
   leur propre fil + composer serait absurde. */
function isFullScreenChatRoute(pathname: string): boolean {
  return pathname.startsWith('/c/') || pathname.endsWith('/chat');
}

function AppHeaderRegion() {
  const config = useAppHeaderConfig();
  const { pathname } = useLocation();
  if (NO_SHELL_HEADER_PATHS.has(pathname) || config.hidden) return null;
  return <AppHeader {...config} />;
}

export const FLOATING_CHAT_CLEARANCE_CLASS = 'pb-[calc(72px+env(safe-area-inset-bottom))]';

function hasFloatingChat(pathname: string): boolean {
  return !NO_SHELL_HEADER_PATHS.has(pathname) && !isFullScreenChatRoute(pathname);
}

function FloatingChatRegion() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  if (!hasFloatingChat(pathname)) return null;
  return (
    <FloatingChat
      open={open}
      onOpenChange={setOpen}
      footer={<span className="text-[13px] text-muted-foreground">Bientôt disponible ici</span>}
    >
      <p className="text-[13px] text-muted-foreground">Bientôt disponible ici.</p>
    </FloatingChat>
  );
}

export default function AppShell() {
  const { pathname } = useLocation();
  const config = useAppHeaderConfig();
  return (
    <HeaderShellRoot active={!NO_SHELL_HEADER_PATHS.has(pathname)} chromeless={config.showBurger}>
      <ExamImportNoticeToast />
      <BulkImportNoticeToast />
      <AppHeaderRegion />
      <div data-testid="shell-content" className={hasFloatingChat(pathname) ? FLOATING_CHAT_CLEARANCE_CLASS : undefined}>
        <Outlet />
      </div>
      <FloatingChatRegion />
    </HeaderShellRoot>
  );
}
